import { useEffect, useState, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Flex,
  Group,
  Loader,
  Paper,
  Stack,
  Title,
} from "@mantine/core";
import { api } from "/@/services/api/api";
import { storeRemoteUploadSession } from "/@/shared/utils/remoteUploadSession";
import {
  useOrderComment,
  useOrderConfiguration,
  useOrderFiles,
} from "@jasper/use-cases";
import { useAuthContext } from "../../../../shared/contexts/AuthContext";
import { Text } from "@mantine/core";
import { DroppableFileInput, JasperWordmark } from "@jasper/forms";
import { notifications } from "@mantine/notifications";
import { IconPhoto } from "@tabler/icons-react";

const RemoteUploadFilePage = ({
  orderId,
  uploadFile,
}: {
  orderId: string;
  uploadFile: (file: File) => Promise<void>;
}) => {
  const { order, isLoading, error } = useOrderConfiguration({ orderId });

  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  if (isLoading) {
    return (
      <Flex align="center" justify="center" h="100%" pb="xl">
        <Loader />
      </Flex>
    );
  }

  if (error || !order) {
    return <Alert color="red">Une erreur est survenue</Alert>;
  }

  const handleSubmit = async () => {
    const result = await Promise.allSettled(
      files.map(file => uploadFile(file))
    );

    const rejectedResults = result.filter(
      (res): res is PromiseRejectedResult => res.status === "rejected"
    );

    if (rejectedResults.length) {
      notifications.show({
        title: "Erreur lors du téléchargement des fichiers",
        message: rejectedResults
          .map(r => r.reason?.message ?? "Unknown error")
          .join(", "),
        color: "red",
      });

      return;
    }

    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <Stack align="center" justify="center" h="100%" gap="md" pb="xl">
        <Text c="green" size="xl" fw={500}>
          Fichiers téléchargés avec succès !
        </Text>
        <Text c="dimmed">Vous pouvez fermer cette fenêtre.</Text>
        <Button
          disabled={!files.length}
          onClick={() => {
            setFiles([]);
            setIsSubmitted(false);
          }}
          w="100%"
          variant="light"
        >
          {"Soumettre d'autres fichiers"}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack maw="70vw" gap="lg">
      <Group gap="xs">
        <Stack gap="xs">
          <Title c="primary" order={5}>
            Commande
          </Title>
          <Title c="primary" order={5}>
            Patient
          </Title>
        </Stack>

        {order.patient && (
          <Stack gap="xs">
            <Text>{order.reference}</Text>
            <Text>
              {order.patient.firstName} {order.patient.lastName}
            </Text>
          </Stack>
        )}
      </Group>

      <DroppableFileInput
        files={files}
        onChange={setFiles}
        imageMode
        acceptedTypes={["image/*"]}
        fileIcon={<IconPhoto />}
        uploadText="Séléctionnez vos images"
      />

      <Button disabled={!files.length} onClick={handleSubmit}>
        Soumettre
      </Button>
    </Stack>
  );
};

// V1 review page: the files go to the active order comment
const OrderCommentRemoteUploadPage = ({ orderId }: { orderId: string }) => {
  const { uploadActiveOrderCommentAttachment } = useOrderComment({ orderId });

  return (
    <RemoteUploadFilePage
      orderId={orderId}
      uploadFile={uploadActiveOrderCommentAttachment}
    />
  );
};

// V2 review page: the files go to the order
const OrderFilesRemoteUploadPage = ({ orderId }: { orderId: string }) => {
  const { uploadOrderFile } = useOrderFiles({ orderId });

  return (
    <RemoteUploadFilePage orderId={orderId} uploadFile={uploadOrderFile} />
  );
};

// Logs the phone in with the token of the QR code link, then shows the upload page
const RemoteUploadLayout = ({
  renderPage,
}: {
  renderPage: (orderId: string) => ReactNode;
}) => {
  const { logIn, isLoggedIn } = useAuthContext();

  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { orderId } = useParams<{ orderId: string }>();

  useEffect(() => {
    if (!orderId) {
      setError("Le lien est invalide ou a expiré.");
      setIsLoading(false);
      return;
    }

    if (token) {
      storeRemoteUploadSession(token);
    }

    void api
      .get("/users/me")
      .then(res => logIn(res.data))
      .catch(() => {
        setError("Le lien est invalide ou a expiré.");
      })
      .finally(() => setIsLoading(false));
  }, [token, orderId]);

  return (
    <Stack align="center" justify="center" h="100vh" bg="primary">
      <Paper shadow="md" p="xl" withBorder>
        <Stack align="center" mb="md">
          <JasperWordmark size={47} height="auto" />
        </Stack>
        {isLoading && (
          <Stack align="center">
            <Loader />
          </Stack>
        )}
        {!isLoading && (error || !orderId) && (
          <Alert color="red">{error}</Alert>
        )}
        {!error && isLoggedIn && !isLoading && orderId && renderPage(orderId)}
      </Paper>
    </Stack>
  );
};

export const RemoteUploadFile = () => (
  <RemoteUploadLayout
    renderPage={orderId => <OrderCommentRemoteUploadPage orderId={orderId} />}
  />
);

export const RemoteOrderFilesUpload = () => (
  <RemoteUploadLayout
    renderPage={orderId => <OrderFilesRemoteUploadPage orderId={orderId} />}
  />
);
